import { isUndefined, isUndefinedOrEmpty } from '../../utils/js.js';
import { parseIsoDateToDate } from '../Utils/dateHelper.js';
import { parse } from '../Utils/numeriHelper.js';
import createAsyncValidator from './asyncValidation.js';
import { ValidationRules, ValidationLevel } from './types.js';

/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-restricted-syntax */
const rules = {};
function getValidationLevel(level) {
    if (!level || level === ValidationLevel.ERROR) {
        return ValidationLevel.ERROR;
    }
    if (level === ValidationLevel.WARNING) {
        return ValidationLevel.WARNING;
    }
    throw new TypeError(`Validation level '${level}' non riconosciuto`);
}
function validate(name, value, additionalParams) {
    const rule = rules[name];
    if (!rule) {
        throw new Error(`Funzione di validazione ${name} sconosciuta`);
    }
    const params = {};
    for (const prop in rule) {
        if (Object.prototype.hasOwnProperty.call(rule, prop)) {
            params[prop] = rule[prop];
        }
    }
    if (additionalParams !== undefined) {
        for (const prop in additionalParams) {
            if (Object.prototype.hasOwnProperty.call(additionalParams, prop)) {
                params[prop] = additionalParams[prop];
            }
        }
    }
    const res = rule.rule(value, params);
    if (res) {
        return params.message;
    }
    return null;
}
function extractValidationResult(result) {
    let message = null;
    let level = ValidationLevel.ERROR;
    if (typeof result === 'string') {
        message = result;
    }
    else if (result === null) {
        message = result;
    }
    else {
        // se la funzione ritorna un oggetto controllo se presente l'attributo warning
        const { message: messageResult, level: levelResult } = result;
        message = messageResult || null;
        level = getValidationLevel(levelResult);
    }
    return { message, level };
}
function mergeValidationResult(message, level, warnings, messages) {
    if (message != null) {
        if (level === ValidationLevel.WARNING) {
            warnings.push(message);
        }
        else {
            messages.push(message);
        }
    }
}
function validateRule(ruleId, value, result) {
    const message = validate(ruleId, value);
    const rule = rules[ruleId];
    const level = getValidationLevel(rule.level);
    mergeValidationResult(message, level, result.warnings, result.messages);
}
function validateCustomFunction(ruleFunction, value, result) {
    const validationResult = ruleFunction(value);
    if (validationResult instanceof Promise) {
        result.asyncResults.push(validationResult);
    }
    else {
        const { message, level } = extractValidationResult(validationResult);
        mergeValidationResult(message, level, result.warnings, result.messages);
    }
}
function validateCustomObject(ruleObject, value, result) {
    const message = validate(ruleObject.rule, value, ruleObject);
    const rule = rules[ruleObject.rule];
    const level = ruleObject.level
        ? getValidationLevel(ruleObject.level)
        : getValidationLevel(rule.level);
    mergeValidationResult(message, level, result.warnings, result.messages);
}
function validateRules(rulesToVal, value) {
    const result = {
        valid: false,
        messages: [],
        warnings: [],
        asyncResults: [],
    };
    if (rulesToVal) {
        let i = 0;
        for (i = 0; i < rulesToVal.length; i += 1) {
            const ruleToVal = rulesToVal[i];
            if (typeof (ruleToVal) === 'string') {
                validateRule(ruleToVal, value, result);
            }
            else if (typeof (ruleToVal) === 'function') {
                const ruleFunction = ruleToVal;
                validateCustomFunction(ruleFunction, value, result);
            }
            else {
                const ruleObject = ruleToVal;
                validateCustomObject(ruleObject, value, result);
            }
        }
    }
    result.valid = result.messages.length === 0;
    return result;
}
function getValidation(validation) {
    if (typeof validation === 'boolean') {
        return { valid: validation };
    }
    return validation;
}
const codiceFiscaleValido = (value) => {
    // se vuota i dati sono validi
    if (value === null || value === undefined) {
        return true;
    }
    // se non è una stringa non è un codice fiscale valido
    if (typeof (value) !== 'string') {
        return false;
    }
    let cf = value;
    let s;
    if (cf === '') {
        return true;
    }
    cf = cf.toUpperCase();
    if (cf.length !== 16) {
        return false;
    }
    const validi = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    for (let i = 0; i < 16; i += 1) {
        if (validi.indexOf(cf.charAt(i)) === -1) {
            return false;
        }
    }
    const set1 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const set2 = 'ABCDEFGHIJABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const setpari = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const setdisp = 'BAKPLCQDREVOSFTGUHMINJWZYX';
    s = 0;
    for (let i = 1; i <= 13; i += 2) {
        s += setpari.indexOf(set2.charAt(set1.indexOf(cf.charAt(i))));
    }
    for (let i = 0; i <= 14; i += 2) {
        s += setdisp.indexOf(set2.charAt(set1.indexOf(cf.charAt(i))));
    }
    if (s % 26 !== cf.charCodeAt(15) - 'A'.charCodeAt(0)) {
        return false;
    }
    return true;
};
const IBANValido = (iban) => {
    // se vuota i dati sono validi
    if (iban === null || iban === undefined) {
        return true;
    }
    // se non è una stringa non è un numero di cellualre valido
    if (typeof (iban) !== 'string') {
        return false;
    }
    if (iban.length === 0) {
        return true;
    }
    let newIban = iban.toUpperCase();
    if (newIban.length < 2 || newIban.substring(0, 2) !== 'IT') {
        return false;
    }
    const modulo = (divident, divisor) => {
        let m = 0;
        for (let i = 0; i < divident.length; i += 1) {
            m = (m * 10 + parseInt(divident.charAt(i), 10)) % divisor;
        }
        return m;
    };
    if (newIban.search(/^[A-Z]{2}/gi) < 0) {
        return false;
    }
    newIban = newIban.substring(4) + newIban.substring(0, 4);
    newIban = newIban.replace(/[A-Z]/g, (match) => `${match.charCodeAt(0) - 55}`);
    return parseInt(`${modulo(newIban, 97)}`, 10) === 1;
};
function verificaCodiceProvinciaPartitaIva(partitaIva) {
    const codiceProvinciale = parseInt(partitaIva.substring(7, 10), 10);
    if ((codiceProvinciale === 0 || codiceProvinciale > 100)
        && codiceProvinciale !== 120
        && codiceProvinciale !== 121
        && codiceProvinciale !== 888
        && codiceProvinciale !== 999) {
        return false;
    }
    return true;
}
const verificaCodiceControlloPartitaIva = (partitaIva) => {
    // calcolo il codice di controllo
    let s = 0;
    let c = 0;
    for (let i = 0; i <= 9; i += 2) {
        s += partitaIva.charCodeAt(i) - '0'.charCodeAt(0);
    }
    for (let i = 1; i <= 9; i += 2) {
        c = 2 * (partitaIva.charCodeAt(i) - '0'.charCodeAt(0));
        if (c > 9) {
            c -= 9;
        }
        s += c;
    }
    // verifico che il codice di controllo sia corretto
    // eslint-disable-next-line no-mixed-operators
    if ((10 - s % 10) % 10 !== partitaIva.charCodeAt(10) - '0'.charCodeAt(0)) {
        return false;
    }
    return true;
};
const partitaIVAValida = (partitaIva) => {
    // se vuota i dati sono validi
    if (partitaIva === null || partitaIva === undefined) {
        return true;
    }
    // se non è una stringa non è una partita IVA valdia
    if (typeof (partitaIva) !== 'string') {
        return false;
    }
    if (partitaIva.length === 0) {
        return true;
    }
    // se la lunghezza non è corretta allora è sicuramente errata
    if (partitaIva.length !== 11) {
        return false;
    }
    // se sono presenti caratteri non numerici allora è errata
    const validi = '0123456789';
    for (let i = 0; i < 11; i += 1) {
        if (validi.indexOf(partitaIva.charAt(i)) === -1) {
            return false;
        }
    }
    if (!verificaCodiceProvinciaPartitaIva(partitaIva)
        || !verificaCodiceControlloPartitaIva(partitaIva)) {
        return false;
    }
    return true;
};
const telefonoValido = (telefono) => {
    // se vuota i dati sono validi
    if (telefono === null || telefono === undefined) {
        return true;
    }
    // se non è una stringa non è un numero di cellualre valido
    if (typeof (telefono) !== 'string') {
        return false;
    }
    if (telefono.length === 0) {
        return true;
    }
    if (telefono.replace(/ /g, '').match(/^[+]?[0|3]{1}[1-9]+[-\s/]?[0-9]{5,}$/i)) {
        return true;
    }
    return false;
};
const validateWeekDay = (date) => {
    // se vuota i dati sono validi
    if (date instanceof Date) {
        if (date.getDay() === 0 /* sunday */ || date.getDay() === 6 /* saturday */) {
            return true;
        }
        return false;
    }
    return true;
};
const mailValida = (mail) => {
    // se vuota i dati sono validi
    if (mail === null || mail === undefined) {
        return true;
    }
    // se non è una stringa non è una email valida
    if (typeof (mail) !== 'string') {
        return false;
    }
    if (mail.length === 0) {
        return true;
    }
    // https://www.abstractapi.com/guides/email-address-pattern-validation
    if (mail
        // eslint-disable-next-line max-len, no-control-regex
        .match(/^([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x22([^\x0d\x22\x5c\x80-\xff]|\x5c[\x00-\x7f])*\x22)(\x2e([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x22([^\x0d\x22\x5c\x80-\xff]|\x5c[\x00-\x7f])*\x22))*\x40([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x5b([^\x0d\x5b-\x5d\x80-\xff]|\x5c[\x00-\x7f])*\x5d)(\x2e([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x5b([^\x0d\x5b-\x5d\x80-\xff]|\x5c[\x00-\x7f])*\x5d))*$/i) // NOSONAR
    ) {
        return true;
    }
    return false;
};
/**
   * Versione di validazione della mail "stringente" compatibile con la validazione lato backend.
   * @param mail mail
   * @returns true se la mail è valida
   */
const mailValidaStrict = (mail) => {
    // se vuota i dati sono validi
    if (mail === null || mail === undefined) {
        return true;
    }
    // se non è una stringa non è una email valida
    if (typeof (mail) !== 'string') {
        return false;
    }
    if (mail.length === 0) {
        return true;
    }
    // https://www.abstractapi.com/guides/email-address-pattern-validation
    // eslint-disable-next-line max-len
    if (mail.match(/^(?!\.)[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]{1,64}@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,62}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,62}[a-zA-Z0-9])?)+$/i)) {
        return true;
    }
    return false;
};
const Validation = {
    DefaultRules: ValidationRules,
    Levels: ValidationLevel,
    register(name, rule, params) {
        rules[name] = Object.assign(Object.assign({}, params), { rule });
    },
    validate,
    getValidationLevel,
    validateAll(rulesToVal, value) {
        const { valid, messages, warnings, asyncResults, } = validateRules(rulesToVal, value);
        if (asyncResults.length > 0) {
            throw new TypeError('Validation asincrone non supportata');
        }
        return {
            valid, messages, warnings,
        };
    },
    validateAsyncAll(rulesToVal, value) {
        const { messages, warnings, asyncResults, } = validateRules(rulesToVal, value);
        if (asyncResults.length === 0) {
            return {
                valid: messages.length === 0,
                messages,
                warnings,
            };
        }
        return Promise.allSettled(asyncResults).then((results) => {
            for (const result of results) {
                if (result.status === 'fulfilled') {
                    const { message, level } = extractValidationResult(result.value);
                    mergeValidationResult(message, level, warnings, messages);
                }
                else if (result.reason instanceof Error) {
                    messages.push(result.reason.message);
                }
                else if (typeof result.reason === 'string') {
                    messages.push(result.reason);
                }
                else {
                    messages.push('Errore validazione');
                }
            }
        }).then(() => ({
            valid: messages.length === 0,
            messages,
            warnings,
        }));
    },
    createAsyncValidator,
    codiceFiscaleValido,
    IBANValido,
    partitaIVAValida,
    telefonoValido,
    mailValida,
    mailValidaBackend: mailValidaStrict,
};
Validation.register(ValidationRules.required, (x) => !(`${x || ''}`).trim(), { message: 'Campo obbligatorio' });
Validation.register(ValidationRules.minLength, (x, y) => (!isUndefined(y === null || y === void 0 ? void 0 : y.minLength) && (`${x}`).length < y.minLength), { message: 'Campo troppo corto' });
Validation.register(ValidationRules.maxLength, (x, y) => (!isUndefined(y === null || y === void 0 ? void 0 : y.maxLength) && (`${x}`).length > y.maxLength), { message: 'Campo troppo lungo' });
Validation.register(ValidationRules.maxCharacters, (x, y) => {
    if (y && y.maxCharacters) {
        return (y.maxCharacters && (`${x}`).length > y.maxCharacters);
    }
    return false;
}, { message: 'Hai terminato i caratteri disponibili' });
Validation.register(ValidationRules.CodiceFiscaleAlfaNumerico, (x) => !codiceFiscaleValido(`${x || ''}`), { message: 'Codice Fiscale non valido' });
Validation.register(ValidationRules.PartitaIva, (x) => !partitaIVAValida(x || ''), { message: 'Partita IVA non valida' });
Validation.register(ValidationRules.CodiceFiscale, (x) => !partitaIVAValida(`${x || ''}`) && !codiceFiscaleValido(`${x}`), { message: 'Codice Fiscale non valido' });
Validation.register(ValidationRules.IBAN, (x) => !IBANValido(`${x || ''}`), { message: 'IBAN non valido' });
Validation.register(ValidationRules.Telefono, (x) => !telefonoValido(`${x || ''}`), { message: 'Telefono non valido' });
Validation.register(ValidationRules.Fax, (x) => !telefonoValido(`${x || ''}`), { message: 'Fax non valido' });
Validation.register(ValidationRules.Email, (x) => !mailValida(`${x || ''}`), { message: 'Email non valida' });
Validation.register(ValidationRules.EmailStrict, (x) => !mailValidaStrict(`${x || ''}`), { message: 'Email non valida' });
Validation.register(ValidationRules.Range, (x, y) => {
    let val = null;
    if (x != null && x !== '') {
        val = parse(x);
    }
    if (isUndefinedOrEmpty(val)) {
        return false;
    }
    if (!isUndefined(y === null || y === void 0 ? void 0 : y.maxValue) && val > y.maxValue) {
        return true;
    }
    if (!isUndefined(y === null || y === void 0 ? void 0 : y.minValue) && val < y.minValue) {
        return true;
    }
    return false;
}, { message: "Valore non compreso nell'intervallo" });
Validation.register(ValidationRules.Pattern, (x, y) => {
    if (y === null || y === void 0 ? void 0 : y.pattern) {
        return !y.pattern.test(x);
    }
    return false;
}, { message: 'Valore non corrispondente al pattern' });
Validation.register(ValidationRules.DateRange, (x) => {
    if (Array.isArray(x)) {
        const range = [0, 1].map((index) => {
            if (typeof x[index] === 'string') {
                return parseIsoDateToDate(x[index]);
            }
            if (x[index] instanceof Date) {
                return x[index];
            }
            return null;
        });
        if (range[0] === range[1]) {
            return false;
        }
        return range[0] !== null && range[1] !== null && range[0].getTime() > range[1].getTime();
    }
    return true;
}, { message: 'La prima data non è antecedente alla seconda' });
Validation.register(ValidationRules.NumberRange, (x) => Array.isArray(x) && x[0] > x[1], { message: 'La prima cifra non è inferiore alla seconda' });
Validation.register(ValidationRules.weekdays, validateWeekDay, { message: 'La data deve essere un giorno della settimana' });
function isWeekdaysValidation(validation) {
    return validation === ValidationRules.weekdays;
}

export { Validation as default, getValidation, isWeekdaysValidation };
